import { useEffect, useId, useRef, useState, useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";
import { FiAlertCircle, FiAlertTriangle, FiCheck, FiChevronDown, FiClock, FiRefreshCw } from "react-icons/fi";
import {
  clearFinishedSteps,
  getStepsSnapshot,
  ServerStep,
  StepRequest,
  StepStatus,
  subscribeSteps,
} from "../../../Services/requestSteps";
import "./loadingSteps.scss";

// ─── Step loader ──────────────────────────────────────────────────────────────
// A centred panel that lists what the current page is still waiting for, one
// line per request (Services/requestSteps). It sits over the skeletons, not in
// place of them. It is fixed in the centre of the screen so it never moves the
// page, and mounted once in App, so no page wires anything. It has no close
// or minimize: it goes away by itself once the page has loaded.

// A quick load never shows the panel.
const SHOW_AFTER_MS = 700;
// Chained requests (the shell's, then the page's, then a tab's) leave short
// gaps. A gap shorter than this keeps them in one list.
const JOIN_GAP_MS = 400;
// How long a finished list stays on screen. The pointer resting on it holds
// it there, and the count starts again when the pointer leaves.
const DONE_HOLD_MS = 1500;
const FAILED_HOLD_MS = 5000;
// The fade at the end of a hold; the stylesheet's $steps-fade matches it.
const FADE_MS = 300;
const SLOW_MS = 8000;
const VERY_SLOW_MS = 45000;

interface StepLine {
  key: string;
  label: string;
  detail?: string;
  status: StepStatus;
  startedAt: number;
  endedAt?: number;
  serverSteps?: ServerStep[];
}

interface Hint {
  tone: "info" | "warn" | "error";
  text: string;
  reload: boolean;
}

const STATUS_TEXT: Record<StepStatus, string> = {
  pending: "loading",
  done: "loaded",
  failed: "failed",
};

// One line per key. A refetch or a duplicate request joins its line: the line
// is loading while any of them is, and otherwise shows the latest result.
const toLines = (entries: StepRequest[]): StepLine[] => {
  const groups = new Map<string, StepRequest[]>();
  entries.forEach((entry) => {
    const group = groups.get(entry.key);
    if (group) group.push(entry);
    else groups.set(entry.key, [entry]);
  });
  return Array.from(groups.values()).map((group) => {
    const pending = group.some((r) => r.status === "pending");
    const latest = group.reduce((a, b) => ((b.endedAt ?? 0) >= (a.endedAt ?? 0) ? b : a));
    // The newest request still loading that has reported steps speaks for the
    // line; an older duplicate's steps are about a load already replaced.
    const reporting = group
      .filter((r) => r.status === "pending" && r.serverSteps?.length)
      .reduce<StepRequest | null>((a, b) => (!a || b.startedAt >= a.startedAt ? b : a), null);
    return {
      key: group[0].key,
      label: latest.label,
      detail: latest.detail,
      status: pending ? "pending" : latest.status,
      startedAt: Math.min(...group.map((r) => r.startedAt)),
      endedAt: pending ? undefined : Math.max(...group.map((r) => r.endedAt ?? r.startedAt)),
      serverSteps: reporting?.serverSteps,
    };
  });
};

// How far one line has got, from 0 to 1, for the ring and the line's bar. A
// finished line is whole. A line still loading creeps with time, so a long
// wait never looks frozen, and each step the server reports finished closes
// part of the gap left. The server never says how many steps are coming (all
// it has reported can be done with more to start), so no count of steps makes
// a line whole; only the answer does.
const lineProgress = (line: StepLine, now: number) => {
  if (line.status !== "pending") return 1;
  const waited = 0.5 * (1 - Math.exp(-(now - line.startedAt) / 8000));
  const finished = line.serverSteps?.filter((step) => step.state !== "active").length ?? 0;
  return Math.min(0.95, 1 - (1 - waited) * 0.7 ** finished);
};

const seconds = (ms: number, precise: boolean) => {
  const s = Math.max(0, ms) / 1000;
  if (precise && s < 0.1) return "<0.1 s";
  return precise && s < 10 ? `${s.toFixed(1)} s` : `${Math.floor(s)} s`;
};

// The whole load as a ring. While loading it carries the percentage; a
// finished load swaps it for a tick or a warning.
const RING_BOX = 46;
const RING_STROKE = 4;

const ProgressRing = ({ value, status }: { value: number; status: StepStatus }) => {
  const gradient = `wmx-steps-ring-${useId().replace(/:/g, "")}`;
  const box = RING_BOX;
  const r = (box - RING_STROKE) / 2;
  const circumference = 2 * Math.PI * r;
  return (
    <span className={`wmx-steps-ring wmx-steps-ring--${status}`} aria-hidden="true">
      <svg width={box} height={box} viewBox={`0 0 ${box} ${box}`}>
        <defs>
          <linearGradient id={gradient} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" className="wmx-steps-ring__from" />
            <stop offset="100%" className="wmx-steps-ring__to" />
          </linearGradient>
        </defs>
        <circle className="wmx-steps-ring__track" cx={box / 2} cy={box / 2} r={r} strokeWidth={RING_STROKE} />
        <circle
          className="wmx-steps-ring__arc"
          cx={box / 2}
          cy={box / 2}
          r={r}
          strokeWidth={RING_STROKE}
          stroke={`url(#${gradient})`}
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - value)}
        />
      </svg>
      <span className="wmx-steps-ring__centre">
        {status === "pending" ? (
          `${Math.round(value * 100)}%`
        ) : status === "done" ? (
          <FiCheck size={18} strokeWidth={3} />
        ) : (
          <FiAlertCircle size={18} strokeWidth={2.5} />
        )}
      </span>
    </span>
  );
};

// The server's own steps for a request still loading (backend
// Utility/Progress.js). Finished steps fold into one line so a long request
// does not grow the panel; each running step gets its own line, and several
// run at once where the server reads in parallel. A running step's detail (a
// count that ticks up) is hidden from screen readers, like the timers.
const ServerSteps = ({ steps }: { steps: ServerStep[] }) => {
  const done = steps.filter((step) => step.state === "done");
  const open = steps.filter((step) => step.state !== "done");
  return (
    <ul className="wmx-steps__sub">
      {done.length > 0 && (
        <li className="wmx-steps__subline is-done">
          <FiCheck className="wmx-steps__subicon" size={11} strokeWidth={3} aria-hidden="true" />
          <span className="wmx-steps__sublabel">
            {done.length === 1 ? done[0].label : `${done.length} steps done`}
            {done.length === 1 && done[0].detail && <span className="wmx-steps__subdetail"> · {done[0].detail}</span>}
          </span>
        </li>
      )}
      {open.map((step) => (
        <li key={step.id} className={`wmx-steps__subline is-${step.state}`}>
          {step.state === "active" ? (
            <span className="wmx-steps__subicon wmx-steps__subspinner" aria-hidden="true" />
          ) : (
            <FiAlertCircle className="wmx-steps__subicon" size={11} strokeWidth={3} aria-hidden="true" />
          )}
          <span className="wmx-steps__sublabel">
            {step.label}
            {step.state === "failed" && <span className="visually-hidden">, failed</span>}
            {step.detail && (
              <span className="wmx-steps__subdetail" aria-hidden="true">
                {" "}
                · {step.detail}
              </span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
};

// One request. A line with more to show (the server's steps, or what the
// request does) opens and closes on click. While it loads it also draws a thin
// bar: filling as the server reports steps, sweeping when it reports none.
const Line = ({
  line,
  now,
  open,
  onToggle,
}: {
  line: StepLine;
  now: number;
  open: boolean;
  onToggle: () => void;
}) => {
  const pending = line.status === "pending";
  const steps = pending ? line.serverSteps : undefined;
  const expandable = !!steps || !!line.detail;
  const head = (
    <>
      <span className="wmx-steps__icon" aria-hidden="true">
        {pending ? (
          <span className="wmx-steps__spinner" />
        ) : line.status === "done" ? (
          <FiCheck size={12} strokeWidth={3} />
        ) : (
          <FiAlertCircle size={12} strokeWidth={3} />
        )}
      </span>
      <span className="wmx-steps__label">
        {line.label}
        <span className="visually-hidden">, {STATUS_TEXT[line.status]}</span>
      </span>
      <span className="wmx-steps__time" aria-hidden="true">
        {pending
          ? seconds(now - line.startedAt, false)
          : line.status === "done"
          ? seconds((line.endedAt ?? now) - line.startedAt, true)
          : "Failed"}
      </span>
      <FiChevronDown className={`wmx-steps__chevron${expandable ? "" : " is-blank"}`} size={14} aria-hidden="true" />
    </>
  );
  return (
    <li className={`wmx-steps__line is-${line.status}${open && expandable ? " is-open" : ""}`}>
      {expandable ? (
        <button type="button" className="wmx-steps__linehead" aria-expanded={open} onClick={onToggle}>
          {head}
        </button>
      ) : (
        <div className="wmx-steps__linehead">{head}</div>
      )}
      {(pending || (open && expandable)) && (
        <div className="wmx-steps__body">
          {pending && (
            <span className={`wmx-steps__track${steps ? "" : " is-sweeping"}`} aria-hidden="true">
              <span style={steps ? { width: `${lineProgress(line, now) * 100}%` } : undefined} />
            </span>
          )}
          {open &&
            (steps ? <ServerSteps steps={steps} /> : line.detail && <p className="wmx-steps__detail">{line.detail}</p>)}
        </div>
      )}
    </li>
  );
};

const LoadingSteps = () => {
  const all = useSyncExternalStore(subscribeSteps, getStepsSnapshot);
  const { pathname } = useLocation();
  const [now, setNow] = useState(() => Date.now());
  const [shown, setShown] = useState(false);
  // Lines the user opened or closed in this load, by key. The rest are open
  // while they load and closed once done.
  const [opened, setOpened] = useState<Record<string, boolean>>({});
  const [hovered, setHovered] = useState(false);
  const [hoverEndedAt, setHoverEndedAt] = useState(0);
  const [leaving, setLeaving] = useState(false);
  // The highest the ring has shown for this list, so it never slips back
  // while the same lines load. A line joining starts it again: there is more
  // to do than there was.
  const peak = useRef({ list: "", value: 0 });

  const lines = toLines(all.filter((r) => r.route === pathname));
  const pendingLines = lines.filter((l) => l.status === "pending");
  const busy = pendingLines.length > 0;
  const hasLines = lines.length > 0;
  const runStart = hasLines ? Math.min(...lines.map((l) => l.startedAt)) : null;
  const lastEnd = hasLines && !busy ? Math.max(...lines.map((l) => l.endedAt ?? 0)) : null;
  const anyFailed = lines.some((l) => l.status === "failed");
  const visible = shown && hasLines;
  // The pointer resting on a finished list holds it on screen.
  const held = visible && hovered;
  const holdFrom = lastEnd === null ? null : Math.max(lastEnd, hoverEndedAt);
  const holdFor = shown ? (anyFailed ? FAILED_HOLD_MS : DONE_HOLD_MS) : JOIN_GAP_MS;

  const listKey = `${runStart}:${lines.length}:${busy}`;
  if (peak.current.list !== listKey) peak.current = { list: listKey, value: 0 };
  const estimate = busy ? lines.reduce((sum, line) => sum + lineProgress(line, now), 0) / lines.length : 1;
  const progress = Math.max(estimate, peak.current.value);
  peak.current.value = progress;

  // A clock for the timers and the thresholds, running only while there is
  // something to time.
  useEffect(() => {
    if (!hasLines) return undefined;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(timer);
  }, [hasLines]);

  // A new page starts hidden, with the delay to wait out again.
  useEffect(() => {
    setShown(false);
    setOpened({});
  }, [pathname]);

  useEffect(() => {
    if (!hasLines && shown) setShown(false);
    else if (!shown && busy && runStart !== null && now - runStart >= SHOW_AFTER_MS) setShown(true);
  }, [hasLines, shown, busy, runStart, now]);

  // The panel under the pointer has gone, and an element that unmounts never
  // reports the pointer leaving.
  useEffect(() => {
    setHovered(false);
  }, [visible]);

  // The page has finished: keep the result up for a moment if it was on
  // screen, fade it, then clear it so the next load starts a fresh list.
  // Timers rather than the clock, so a late tick can never skip the fade.
  useEffect(() => {
    if (holdFrom === null || held) return undefined;
    const left = holdFor - (Date.now() - holdFrom);
    const fade = window.setTimeout(() => setLeaving(true), left - FADE_MS);
    const clear = window.setTimeout(() => {
      clearFinishedSteps();
      setShown(false);
      setOpened({});
    }, left);
    return () => {
      window.clearTimeout(fade);
      window.clearTimeout(clear);
      setLeaving(false);
    };
  }, [holdFrom, held, holdFor]);

  if (!visible) return null;

  const status: StepStatus = busy ? "pending" : anyFailed ? "failed" : "done";
  const failedCount = lines.filter((l) => l.status === "failed").length;
  const loadedCount = lines.length - pendingLines.length - failedCount;
  const elapsed = (lastEnd ?? now) - (runStart ?? now);
  const longestWait = busy ? Math.max(...pendingLines.map((l) => now - l.startedAt)) : 0;
  const title = busy ? "Loading this page" : anyFailed ? "Some data didn't load" : "All loaded";
  const count = `${loadedCount} of ${lines.length} loaded${failedCount > 0 ? `, ${failedCount} failed` : ""}`;
  // A failed list often still draws (zeros, "No data"), so this panel can be
  // the only place that says something went wrong.
  const hint: Hint | null =
    anyFailed && !busy
      ? { tone: "error", text: 'What failed may show as zeros or "No data" on the page.', reload: true }
      : longestWait >= VERY_SLOW_MS
      ? {
          tone: "warn",
          text: "This is taking much longer than usual. If it does not finish soon, reload the page.",
          reload: true,
        }
      : longestWait >= SLOW_MS
      ? {
          tone: "info",
          text: "Large accounts and long date ranges take longer. Still working, nothing you need to do.",
          reload: false,
        }
      : null;

  return (
    <div
      className={`wmx-steps wmx-steps--${status}${leaving ? " is-leaving" : ""}`}
      role="status"
      aria-live="polite"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {
        setHovered(false);
        setHoverEndedAt(Date.now());
      }}
    >
      <div className="wmx-steps__head">
        <ProgressRing value={progress} status={status} />
        <div className="wmx-steps__heading">
          <div className="wmx-steps__title">{title}</div>
          <div className="wmx-steps__count">
            {count}
            {status !== "failed" && (
              <span aria-hidden="true">
                {busy ? ` · ${seconds(elapsed, false)}` : ` in ${seconds(elapsed, true)}`}
              </span>
            )}
          </div>
        </div>
      </div>
      <ul className="wmx-steps__list">
        {lines.map((line) => {
          const open = opened[line.key] ?? line.status === "pending";
          return (
            <Line
              key={line.key}
              line={line}
              now={now}
              open={open}
              onToggle={() => setOpened((prev) => ({ ...prev, [line.key]: !open }))}
            />
          );
        })}
      </ul>
      {hint && (
        <div className={`wmx-steps__hint wmx-steps__hint--${hint.tone}`}>
          {hint.tone === "info" ? (
            <FiClock size={14} aria-hidden="true" />
          ) : hint.tone === "warn" ? (
            <FiAlertTriangle size={14} aria-hidden="true" />
          ) : (
            <FiAlertCircle size={14} aria-hidden="true" />
          )}
          <p className="wmx-steps__hinttext">{hint.text}</p>
          {hint.reload && (
            <button type="button" className="wmx-steps__hintaction" onClick={() => window.location.reload()}>
              <FiRefreshCw size={12} aria-hidden="true" />
              Reload
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default LoadingSteps;
